import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { Utils } from "mapbox-gl-draw-snap-mode";

import { approxEqual } from "fond/utils/number";

import simpleSelect from "../../base/simpleSelect";

const { snap, createSnapList } = Utils;
const {
  constants: Constants,
  lib: {
    CommonSelectors: { isFeature },
  },
} = MapboxDraw;

interface DrawCustomModeState {
  canDragMove: boolean;
  dragMoveLocation: mapboxgl.LngLatLike | null;
  initialDragPanState: boolean;
  map: mapboxgl.Map;
  snapList: mapboxgl.MapboxGeoJSONFeature[];
  vertices: mapboxgl.MapboxGeoJSONFeature[];
  feature: MapboxDraw.DrawFeature;
  options: MapboxDraw.MapboxDrawOptions;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
}

const simpleSelectHubs: DrawCustomModeExtension<DrawCustomModeState> = {
  ...simpleSelect,

  /**
   * We fire a custom update event so that we can track when the hub has
   * been moved.
   */
  onMouseUp: function (state, event) {
    if (state.canDragMove && state.dragMoveLocation) {
      this.map.fire(Constants.events.UPDATE, {
        action: Constants.updateActions.MOVE,
        features: this.getSelected().map((f) => f.toGeoJSON()),
      });
    }

    simpleSelect.onMouseUp?.call(this, state, event);
  },

  /**
   * We customise the onMouseDown event to prepare the feature to be snapped during
   * the dragMove event.
   *
   * We do not support multiple selections at all (either shift clicking or box selection). Onle
   * one hub can be selected and dragged at a time.
   */
  onMouseDown: function (state, event) {
    state.initialDragPanState = this.map.dragPan.isEnabled();
    if (isFeature(event)) {
      // select the feature being clicked
      this.clickOnFeature?.(state, event);

      // Augment the state with snapping information
      const selectedFeatures = this.getSelected();
      const [snapList, vertices] = createSnapList(this.map, this._ctx.api, selectedFeatures, this._ctx.options.snapOptions?.snapGetFeatures);

      state.map = this.map;
      state.snapList = snapList;
      state.vertices = vertices;
      state.feature = selectedFeatures?.[0];
      state.options = this._ctx.options;

      return this.startOnActiveFeature?.(state, event);
    }

    return undefined;
  },

  /**
   * On drag get the snap coordinates & determine if the new coordinates are
   * actually snapping to a feature, if so update the coordinates of the event to perform the
   * snap. If no snapping is occuring ignore the event to prevent the feature being
   * moved off a snapTo feature
   */
  dragMove: function (state, event) {
    const { lng, lat } = snap(state, event);

    // Note that the presicion of the event lngLat is higher than that of the
    // features stored within the drawControl so we only check that they are mostly equal
    const hasSnapped = !approxEqual(lng, event.lngLat.lng) || !approxEqual(lat, event.lngLat.lat);
    if (hasSnapped) state.feature.updateCoordinate("", lng, lat);
  },

  onTrash: function () {
    // Do nothing
  },
};

export default simpleSelectHubs;
